import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

type UserRole = 'instructor' | 'student';

interface Assignment {
  id: string;
  title: string;
  description: string;
  access_code: string;
  created_at: string;
  created_by: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState<UserRole>('student');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch assignments based on user role
  useEffect(() => {
    const fetchAssignments = async () => {
      setLoading(true);
      setError(null);
      try {
        let data;
        let error;
        
        if (role === 'instructor') {
          // Fetch assignments created by the user
          const response = await supabase
            .from('assignments')
            .select('*')
            .eq('created_by', user?.id);
            
          data = response.data;
          error = response.error;
        } else {
          // First get the assignment IDs the user has access to
          const accessResponse = await supabase
            .from('assignment_access')
            .select('assignment_id')
            .eq('user_id', user?.id);
            
          if (accessResponse.error) throw accessResponse.error;
          
          // Then fetch the assignments with those IDs
          if (accessResponse.data && accessResponse.data.length > 0) {
            const assignmentIds = accessResponse.data.map(item => item.assignment_id);
            
            const assignmentsResponse = await supabase
              .from('assignments')
              .select('*')
              .in('id', assignmentIds);
              
            data = assignmentsResponse.data;
            error = assignmentsResponse.error;
          } else {
            // No assignments joined
            data = [];
            error = null;
          }
        }
        
        if (error) throw error;
        
        setAssignments(data || []);
      } catch (error: any) {
        console.error('Error fetching assignments:', error);
        setError(error.message || 'Failed to fetch assignments');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchAssignments();
    }
  }, [user, role]);

  const handleRoleChange = (newRole: UserRole) => {
    setRole(newRole);
  };

  const handleCreateAssignment = () => {
    navigate('/create-assignment');
  };

  const handleJoinAssignment = () => {
    navigate('/join-assignment');
  };

  const handleViewAssignment = (assignmentId: string) => {
    navigate(`/assignment/${assignmentId}`);
  };

  return (
    <div className="bg-white">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-primary">Dashboard</h1>
          
          <div className="flex items-center space-x-4">
            <div className="bg-gray-100 rounded-lg shadow-sm p-1">
              <button
                className={`px-4 py-2 rounded-md ${
                  role === 'student'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700'
                }`}
                onClick={() => handleRoleChange('student')}
              >
                Student
              </button>
              <button
                className={`px-4 py-2 rounded-md ${
                  role === 'instructor'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700'
                }`}
                onClick={() => handleRoleChange('instructor')}
              >
                Instructor
              </button>
            </div>
            
            {role === 'instructor' ? (
              <button
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
                onClick={handleCreateAssignment}
              >
                Create Assignment
              </button>
            ) : (
              <button
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
                onClick={handleJoinAssignment}
              >
                Join Assignment
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {role === 'instructor' ? 'My Assignments' : 'Enrolled Assignments'}
          </h2>
          
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : assignments.length === 0 ? (
            <div className="bg-gray-50 p-8 rounded-lg text-center border border-gray-200">
              <p className="text-gray-600 mb-4">
                {role === 'instructor'
                  ? "You haven't created any assignments yet."
                  : "You haven't joined any assignments yet."}
              </p>
              <button
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
                onClick={role === 'instructor' ? handleCreateAssignment : handleJoinAssignment}
              >
                {role === 'instructor' ? 'Create Your First Assignment' : 'Join an Assignment'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {assignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border border-gray-200"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-primary mb-2 truncate">
                      {assignment.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {assignment.description || 'No description provided'}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">
                        {new Date(assignment.created_at).toLocaleDateString()}
                      </span>
                      {role === 'instructor' && (
                        <div className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-700">
                          Code: {assignment.access_code}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-50 px-6 py-3">
                    <button
                      className="text-primary font-medium hover:underline"
                      onClick={() => handleViewAssignment(assignment.id)}
                    >
                      {role === 'instructor' ? 'Manage Assignment' : 'View Assignment'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
